<template>
  <div
    v-if="isValidCustomFilter"
    class="p-2 filter w-100"
    :class="{
      'has-changes': hasStagedChanges,
      active: stagedFilter.active,
    }"
  >
    <!-- Toggle on/off -->
    <!-- <label v-if="modelValue.fieldPath?.length " class="toggle"> -->
    <label v-if="stagedFilter.active || canSubmit" class="toggle">
      <BooleanToggle v-model="stagedFilter.active" @change="updateFilter()" />
      {{ stagedFilter.active ? appTranslate('Active') : appTranslate('Inactive') }}
    </label>

    <form @submit.prevent="updateFilter(true)">
      <div class="path-hierarchy mb-2">
        <template v-for="(levelFields, i) of fieldHierarchy" :key="i">
          <div class="d-flex w-100">
            <select
              v-model="stagedFilter.fieldPath[i]"
              class="form-control mb-1"
              :class="{ invalid: !stagedFilter.fieldPath[i] }"
              @change="sanitizeFieldPath(i)"
            >
              <option :value="undefined" disabled>
                --
                {{ appTranslate('Choose an option') }}
                --
              </option>
              <option v-for="field of levelFields" :key="field.value" :value="field.value">
                {{ field.displayValue }}
              </option>
            </select>
          </div>
        </template>
      </div>
      <!-- <template v-if="stagedChangesAreValid"> -->
      <hr />
      <select v-model="stagedFilter.operator" class="form-control">
        <option v-for="operator in operators" :key="operator.value" :value="operator.value">
          {{ translate(operator.displayValue, 'application', 'lowercase') }}
        </option>
      </select>

      <div style="gap: 5px" class="inputs mt-2 d-flex flex-wrap" :class="{ invalid: !rangeIsValid }">
        <!-- Range -->
        <template v-if="stagedFilter.operator === 'range'">
          <label class="mb-0">
            <div class="ml-2">
              {{ translate('from', 'application', 'lowercase') }}
            </div>
            <input v-model="firstStagedInput" type="number" class="form-control inline range" min="0" step=".1" />
          </label>
          <label class="mb-0">
            <div class="ml-2">
              {{ translate('to', 'application', 'lowercase') }}
            </div>
            <input v-model="secondStagedInput" type="number" class="form-control inline range" min="0" step=".1" />
          </label>
        </template>

        <template v-else>
          <label class="m-0">
            <input v-model="splitValues" type="checkbox" />
            {{ appTranslate('Multiple values') }}
          </label>

          <!-- Split values -->
          <textarea
            v-if="splitValues"
            v-model="stagedOptionsSplit"
            class="form-control inline w-100"
            rows="5"
            :placeholder="appTranslate('Add multiple search terms separated by line break')"
          />

          <!-- Includes -->
          <textarea v-else-if="stagedFilter.operator === 'includes'" v-model="firstStagedInput" class="form-control inline w-100" rows="2" />

          <!-- Equals/starts or ends with -->
          <input v-else v-model="firstStagedInput" type="text" class="form-control inline" />
        </template>

        <button
          class="btn"
          :class="[stagedFilter.operator === 'includes' || splitValues ? 'w-25' : '', canSubmit ? 'btn-primary' : 'btn-light']"
          :disabled="!canSubmit || !hasStagedChanges"
          type="submit"
        >
          Ok
        </button>
      </div>
    </form>
  </div>
</template>

<script lang="ts" setup>
import { CustomFilter } from '@/models/Filter'
import { Country, Language, Option } from '@/models/Global'
import { camelCaseToTerm, clone, hasValue, objectsAreEqual, splitSearchTerm, toNumber } from '@/utils/tools'
import BooleanToggle from '@/components/simple/BooleanToggle.vue'
import { useDefaultStore } from '@/store'
import { decodeQueryValue } from '@/utils/filterUtils'
import { useTranslation } from '@/composables/useTranslation'
import { computed, onMounted, ref, watch } from 'vue'
import { CountrySpecificFields, CountrySpecifics, ProductInfoType, ProductInfoTypes, SimpleLanguageSpecificFields } from '@/models/Product'

// -----------
// -- Setup --
// -----------
const props = defineProps<{
  modelValue: CustomFilter
}>()

const emit = defineEmits(['change', 'update:modelValue'])

const { translate, appTranslate } = useTranslation()
const store = useDefaultStore()

const createComputedStagedValue = (index: number) =>
  computed({
    get() {
      const value = decodeQueryValue(stagedFilter.value.options[index])
      const num = stagedFilter.value.operator === 'range' ? toNumber(value) : value
      return num || num === 0 ? num : ''
    },
    set(value: string) {
      stagedFilter.value.options[index] = value
    },
  })

// -------------------
// -- Reactive vars --
// -------------------
const stagedFilter = ref<CustomFilter>({
  fieldPath: [],
  operator: 'range',
  active: false,
  options: [],
})
const splitValues = ref(props.modelValue.options?.length && props.modelValue.options?.length > 1) // to add multiple terms split on comma or newline

// --------------------
// -- Computed props --
// --------------------
const level1Fields = computed((): Option[] =>
  ['sku', 'mainLanguage', 'availableInCountries', 'recurrenceId', 'countrySpecific', 'languageSpecific', ...ProductInfoTypes].map((obj) => ({
    value: obj,
    displayValue:
      translate(camelCaseToTerm(obj), ['application', 'attributes'], 'capitalize', undefined, undefined, undefined, false) ||
      translate(obj, ['application', 'attributes'], 'capitalize'),
  })),
)

const level2Fields = computed((): Option[] | undefined => {
  if (stagedFilter.value.fieldPath[0]) {
    const parentKey = stagedFilter.value.fieldPath[0]

    if (parentKey === 'countrySpecific') {
      return store.availableCountries.map((country: Country) => ({
        value: country,
        displayValue: translate(country, 'countries', 'capitalize'),
      }))
    } else if (parentKey === 'languageSpecific') {
      return store.availableLanguages.map((language: Language) => ({
        value: language,
        displayValue: translate(language, 'languages', 'capitalize'),
      }))
    } else if (ProductInfoTypes.includes(parentKey as ProductInfoType)) {
      const options = [
        {
          value: 'weight',
          displayValue: `${translate('weight', 'attributes', 'capitalize')} (g)`,
        },
        {
          value: 'dimensions.width',
          displayValue: `${translate('width', 'attributes', 'capitalize')} (mm)`,
        },
        {
          value: 'dimensions.height',
          displayValue: `${translate('height', 'attributes', 'capitalize')} (mm)`,
        },
        {
          value: 'dimensions.length',
          displayValue: `${translate('length', 'attributes', 'capitalize')} (mm)`,
        },
      ]

      if (stagedFilter.value.fieldPath[0] === 'baseUnit') {
        options.push({
          value: 'isbn',
          displayValue: 'ISBN',
        })
      }

      if (stagedFilter.value.fieldPath[0] !== 'consumerItem') {
        options.push({
          value: 'barcode',
          displayValue: translate('barcode', 'attributes', 'capitalize'),
        })
      }
      return options
    }
  }
  return undefined
})

const level3Fields = computed((): Option[] | undefined => {
  if (stagedFilter.value.fieldPath[1]) {
    switch (stagedFilter.value.fieldPath[0]) {
      case 'countrySpecific': {
        return CountrySpecificFields.map((prop) => ({
          value: prop,
          displayValue: translate(camelCaseToTerm(prop), 'attributes', 'capitalize'),
        }))
      }
      case 'languageSpecific': {
        const options = SimpleLanguageSpecificFields.map((prop) => ({
          value: prop,
          displayValue: translate(camelCaseToTerm(prop), 'attributes', 'capitalize'),
        }))

        const usps = {
          value: 'USPS',
          displayValue: appTranslate('USPs'),
        }

        return [...options, usps]
      }
    }
  }
  return undefined
})

const fieldHierarchy = computed(() => {
  const hierarchy = [level1Fields.value, level2Fields.value, level3Fields.value]
  hierarchy.length = hierarchy.filter((s) => !!s).length
  return hierarchy
})

const operators = computed((): Option[] => {
  return [
    {
      value: 'range',
      displayValue: 'value lies within a numeric range',
    },
    {
      value: 'equals',
      displayValue: 'value exactly equals',
    },
    {
      value: 'includes',
      displayValue: 'value includes text',
    },
    {
      value: 'startsWith',
      displayValue: 'value starts with',
    },
    {
      value: 'endsWith',
      displayValue: 'value ends with',
    },
  ]
})

const sanitizedOptions = computed(() => {
  switch (stagedFilter.value.operator) {
    case 'range': {
      const length = secondStagedInput ? 2 : 1
      return stagedFilter.value.options.slice(0, length)
    }
    default:
      return splitValues.value ? stagedFilter.value.options : [stagedFilter.value.options[0]]
  }
})

const firstStagedInput = createComputedStagedValue(0) // first input field value (from/single/multiple)
const secondStagedInput = createComputedStagedValue(1) // second input field value (range to)

const stagedOptionsSplit = computed({
  get() {
    return decodeQueryValue(stagedFilter.value.options.join('\n')) //.join(',').replace(/,/g, '\n')
  },
  set(value: string) {
    stagedFilter.value.options = splitSearchTerm(value)
  },
})

const hasStagedChanges = computed(() => {
  const obj1 = {
    ...stagedFilter.value,
    fieldPath: stagedFilter.value.fieldPath.map((p) => p?.split('.')).flat(),
    options: sanitizedOptions.value,
  }

  return !objectsAreEqual(obj1, props.modelValue, undefined, false)
})

const rangeIsValid = computed(() => {
  if (stagedFilter.value.operator !== 'range') {
    return true
  }

  const firstInputHasValue = hasValue(firstStagedInput.value)
  const secondInputHasValue = hasValue(secondStagedInput.value)

  return firstInputHasValue && secondInputHasValue ? secondStagedInput.value >= firstStagedInput.value : firstInputHasValue || secondInputHasValue
})

const isValidCustomFilter = computed(() => stagedFilter.value.fieldPath?.every((path, i) => fieldHierarchy.value[i]?.some((v) => v.value === path)))

const canSubmit = computed(
  () => isValidCustomFilter.value && rangeIsValid.value && sanitizedOptions.value.length && stagedFilter.value.fieldPath.length === fieldHierarchy.value.length,
)

// --------------------------
// -- Watchers & Lifecycle --
// --------------------------
watch(
  () => props.modelValue,
  (newValue) => {
    stagedFilter.value = getUpdatedStageFilter(newValue)
  },
  { deep: true },
)

onMounted(() => {
  if (props.modelValue) {
    stagedFilter.value = getUpdatedStageFilter(props.modelValue)
  }
})

// -------------
// -- Methods --
// -------------

const updateFilter = (enable?: boolean) => {
  if (canSubmit.value) {
    if (enable !== undefined) {
      stagedFilter.value.active = enable
    }

    const filter: CustomFilter = {
      ...stagedFilter.value,
      options: stagedFilter.value.active ? sanitizedOptions.value : [],
    }

    emit('change', {
      filter,
      previousFilter: props.modelValue,
    })
    emit('update:modelValue', {
      ...stagedFilter.value,
      options: sanitizedOptions.value,
    })
  }
}

const sanitizeFieldPath = (i: number) => {
  if (stagedFilter.value.fieldPath.length !== fieldHierarchy.value.length) {
    stagedFilter.value.fieldPath.length = i + 1
  }
}

const getUpdatedStageFilter = (filter: CustomFilter) => {
  const customFilter: CustomFilter = clone(filter)

  // makeshift fix for dimensions
  if (customFilter.fieldPath[1] === 'dimensions') {
    customFilter.fieldPath[1] = [customFilter.fieldPath[1], customFilter.fieldPath[2]].join('.')
    customFilter.fieldPath.length = 2
  }

  if (customFilter.options) {
    stagedFilter.value.options = clone(customFilter.options)
  }

  return customFilter
}
</script>

<style lang="scss" scoped>
.path-hierarchy {
  display: flex;
  flex-wrap: wrap;
}

.filter {
  border: 1px solid var(--light-grey);

  &.active {
    border-color: var(--primary);
  }

  &.has-changes {
    border: 1px dashed rgba(255, 136, 0, 0.5);
  }
}

.range {
  width: 6em;
}

input[type='text'] {
  flex: 1 1 75%;
}

select.invalid,
.invalid input[type='number'] {
  border-color: red;
}

button[type='submit'] {
  height: fit-content;
  margin-top: auto;
}
</style>
